'use client';

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useUser } from '@/firebase';
import { useFirestore } from '@/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { resolveUserMembership } from '@/lib/resolve-user-identity';
import type { Membership } from '@/models/membership';
import type { Profile } from '@/models/profile';
import type { PrivacySettings } from '@/models/privacy-settings';
import type { CompetitionType } from '@/models/competitions/types';

/**
 * User document from users collection
 */
interface UserDocument {
  uid: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  isAdmin?: boolean;
  isOfficer?: boolean;
  isMember?: boolean;
  membershipId?: string;
  profileId?: string;
  privacySettingsId?: string;
  privacySettingsConfigured?: boolean;
  emailVerified?: boolean;
  emailVerifiedAt?: any;
  /**
   * Cached mirror of every CompetitionType the user is currently a
   * Contest Director for. Source of truth is the `raceDirectors`
   * Firestore collection — this field is written by the admin
   * race-directors API on assign/unassign so the client doesn't need
   * to query the collection separately just to gate UI.
   */
  cdTypes?: CompetitionType[];
  createdAt?: any;
  updatedAt?: any;
}

/**
 * Complete user data context
 * Provides access to all user-related data in one place
 */
interface UserDataContextType {
  // Firebase Auth user
  user: any | null;

  // User document from users collection
  userDoc: UserDocument | null;

  // Membership from memberships collection
  membership: Membership | null;

  // Profile from profiles collection
  profile: Profile | null;

  // Privacy settings from privacySettings collection
  privacySettings: PrivacySettings | null;

  // Loading states
  loading: boolean;
  userLoading: boolean;
  dataLoading: boolean;

  // Computed properties for easy access
  isAuthenticated: boolean;
  isAdmin: boolean;
  isOfficer: boolean;
  isCD: boolean;                   // Contest Director for at least one race type
  cdTypes: CompetitionType[];      // types this user is CD for (empty array if none)
  isEmailVerified: boolean;        // email has been verified
  hasActiveMembership: boolean;    // membership exists AND status is Active
  hasPendingMembership: boolean;   // membership exists AND status is Pending
  hasLinkedMembership: boolean;    // has membershipId in user doc
  hasProfile: boolean;              // has profileId in user doc

  // Refresh function to reload all data
  refresh: () => Promise<void>;
}

const UserDataContext = createContext<UserDataContextType | undefined>(undefined);

/**
 * Provider component that loads and provides all user data
 */
export function UserDataProvider({ children }: { children: ReactNode }) {
  const { user, loading: userLoading } = useUser();
  const firestore = useFirestore();

  const [userDoc, setUserDoc] = useState<UserDocument | null>(null);
  const [membership, setMembership] = useState<Membership | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [privacySettings, setPrivacySettings] = useState<PrivacySettings | null>(null);
  // Start with loading true to prevent flash of "not authenticated" state
  // while checking if we need to load user data
  const [dataLoading, setDataLoading] = useState(true);
  const [loadedUserId, setLoadedUserId] = useState<string | null>(null);

  const loadUserData = async () => {
    if (!user) {
      setUserDoc(null);
      setMembership(null);
      setProfile(null);
      setPrivacySettings(null);
      setLoadedUserId(null);
      setDataLoading(false);
      return;
    }

    // Ensure loading is true while fetching
    setDataLoading(true);

    try {
      // Load user document
      const userDocRef = doc(firestore, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data() as UserDocument;
        setUserDoc(userData);

        // Resolve THE membership tied to this auth user via the shared
        // helper. It queries by linkedUserId and — when more than one
        // membership matches the same uid (primary-managed dependent case)
        // — prefers the primary. Avoids trusting the historically-drifty
        // `users/{uid}.membershipId` cached pointer.
        let resolvedMembership: Membership | null = null;
        try {
          resolvedMembership = await resolveUserMembership(firestore, user.uid);
        } catch (err) {
          console.warn('[useUserData] resolveUserMembership failed', err);
        }
        // Last-resort fallback: cached pointer (for the rare case where the
        // user has a membership but it lacks linkedUserId, e.g. a freshly
        // imported record awaiting the link step).
        if (!resolvedMembership && userData.membershipId) {
          const cachedSnap = await getDoc(doc(firestore, 'memberships', userData.membershipId));
          if (cachedSnap.exists()) {
            resolvedMembership = { id: cachedSnap.id, ...cachedSnap.data() } as Membership;
          }
        }
        setMembership(resolvedMembership);

        // Profile follows the resolved membership when possible (so a
        // mis-cached `users.profileId` doesn't drag the dependent's profile
        // into "your" actions). Fall back to the cached pointer only when
        // the membership doesn't carry a profileId.
        const targetProfileId =
          (resolvedMembership as Membership | null)?.profileId ?? userData.profileId ?? null;
        if (targetProfileId) {
          const profileSnap = await getDoc(doc(firestore, 'profiles', targetProfileId));
          if (profileSnap.exists()) {
            setProfile({ id: profileSnap.id, ...profileSnap.data() } as Profile);
          } else {
            setProfile(null);
          }
        } else {
          setProfile(null);
        }

        // Load privacy settings if linked
        if (userData.privacySettingsId) {
          const privacyRef = doc(firestore, 'privacySettings', userData.privacySettingsId);
          const privacySnap = await getDoc(privacyRef);
          if (privacySnap.exists()) {
            setPrivacySettings({ id: privacySnap.id, ...privacySnap.data() } as PrivacySettings);
          } else {
            setPrivacySettings(null);
          }
        } else {
          setPrivacySettings(null);
        }
      } else {
        setUserDoc(null);
        setMembership(null);
        setProfile(null);
        setPrivacySettings(null);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      // Mark this user as loaded to resolve the loading state
      setLoadedUserId(user?.uid ?? null);
      setDataLoading(false);
    }
  };

  // Load data when user changes
  useEffect(() => {
    loadUserData();
  }, [user]);

  // Computed properties
  const isAuthenticated = !!user;
  const isAdmin = userDoc?.isAdmin === true;
  const isOfficer = userDoc?.isOfficer === true;
  const cdTypes = (userDoc?.cdTypes ?? []) as CompetitionType[];
  const isCD = cdTypes.length > 0;
  const isEmailVerified = userDoc?.emailVerified === true;
  const hasActiveMembership = membership?.status === 'Active';
  const hasPendingMembership = membership?.status === 'Pending';
  // Derive these from the actually-resolved entities so a stale/incorrect
  // cached pointer in users/{uid} doesn't lie to consumers.
  const hasLinkedMembership = !!membership;
  const hasProfile = !!profile;

  // Derive loading state to cover the gap between user auth change and data fetch
  const isLoading = userLoading || dataLoading || ((user?.uid ?? null) !== loadedUserId);

  const value: UserDataContextType = {
    user,
    userDoc,
    membership,
    profile,
    privacySettings,
    loading: isLoading,
    userLoading,
    dataLoading,
    isAuthenticated,
    isAdmin,
    isOfficer,
    isCD,
    cdTypes,
    isEmailVerified,
    hasActiveMembership,
    hasPendingMembership,
    hasLinkedMembership,
    hasProfile,
    refresh: loadUserData,
  };

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
}

/**
 * Hook to access user data context
 * Provides all user-related data in one place
 * 
 * @example
 * const { user, membership, profile, hasActiveMembership } = useUserData();
 * 
 * if (hasActiveMembership) {
 *   // Show member content
 * }
 */
export function useUserData() {
  const context = useContext(UserDataContext);
  if (context === undefined) {
    throw new Error('useUserData must be used within a UserDataProvider');
  }
  return context;
}
